import { useEffect, useState } from "react";
import { UserPlus, UserCheck, GraduationCap, Users, FileText, StickyNote } from "lucide-react";
import { getAllClasses, getAllSections } from "../api/student/studentApi";
import {
  getAllClassesForPortal,
  getAllSectionsForPortal,
} from "../api/student/studentPortalApi";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Input,
  Select,
  Button,
  Checkbox,
} from "../components/ui";

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const BLOOD_GROUP_OPTIONS = [
  { value: "", label: "Select Blood Group" },
  ...["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bg) => ({
    value: bg,
    label: bg,
  })),
];

const TRANSPORT_OPTIONS = [
  { value: "PARENTS", label: "Parents" },
  { value: "SELF", label: "Self" },
  { value: "SCHOOL_TRANSPORT", label: "School Transport" },
];

const DOCUMENT_ITEMS = [
  { key: "birthCertificate", label: "Birth Certificate" },
  { key: "transferCertificate", label: "Transfer Certificate" },
  { key: "aadhaarCard", label: "Aadhaar Card" },
  { key: "photographs", label: "Photographs" },
  { key: "reportCard", label: "Report Card" },
  { key: "other", label: "Other" },
];

const StudentForm = ({
  onSubmit,
  loading,
  isPortal = false,
  initialData = null,
  isEdit = false,
}) => {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [form, setForm] = useState({
    admissionNo: "",
    rollNumber: "",
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "Male",
    dateOfBirth: "",
    bloodGroup: "",
    category: "",
    classId: "",
    sectionId: "",
    fatherName: "",
    motherName: "",
    guardianName: "",
    primaryContact: "",
    secondaryContact: "",
    email: "",
    address: "",
    transportMode: "PARENTS",
    enrolledOn: "",
    remarks: "",
    documentsSubmitted: {
      birthCertificate: false,
      transferCertificate: false,
      aadhaarCard: false,
      photographs: false,
      reportCard: false,
      other: false,
    },
  });

  // Populate form when initialData is available (edit mode)
  useEffect(() => {
    if (!initialData) return;
    const toDateInput = (val) => {
      if (!val) return "";
      try {
        return new Date(val).toISOString().split("T")[0];
      } catch {
        return "";
      }
    };
    setForm({
      admissionNo: initialData.admissionNo || "",
      rollNumber: initialData.rollNumber || "",
      firstName: initialData.firstName || "",
      middleName: initialData.middleName || "",
      lastName: initialData.lastName || "",
      gender: initialData.gender || "Male",
      dateOfBirth: toDateInput(initialData.dateOfBirth),
      bloodGroup: initialData.bloodGroup || "",
      category: initialData.category || "",
      classId: initialData.classId?._id || initialData.classId || "",
      sectionId: initialData.sectionId?._id || initialData.sectionId || "",
      fatherName: initialData.fatherName || "",
      motherName: initialData.motherName || "",
      guardianName: initialData.guardianName || "",
      primaryContact: initialData.primaryContact || "",
      secondaryContact: initialData.secondaryContact || "",
      email: initialData.email || "",
      address: initialData.address || "",
      transportMode: initialData.transportMode || "PARENTS",
      enrolledOn: toDateInput(initialData.enrolledOn),
      remarks: initialData.remarks || "",
      documentsSubmitted: {
        birthCertificate: false,
        transferCertificate: false,
        aadhaarCard: false,
        photographs: false,
        reportCard: false,
        other: false,
        ...(initialData.documentsSubmitted || {}),
      },
    });
  }, [initialData]);

  // Load classes on mount
  useEffect(() => {
    const loadClasses = async () => {
      try {
        console.log("[StudentForm] Loading classes — isPortal:", isPortal);
        const data = isPortal
          ? await getAllClassesForPortal()
          : await getAllClasses();
        console.log("[StudentForm] Classes received:", data);
        setClasses(data || []);
      } catch (err) {
        console.error("[StudentForm] Failed to load classes:", err?.response?.status, err?.response?.data);
      }
    };
    loadClasses();
  }, [isPortal]);

  // Load sections when classId changes
  useEffect(() => {
    if (!form.classId) {
      setSections([]);
      return;
    }
    const loadSections = async () => {
      try {
        console.log("[StudentForm] Loading sections for classId:", form.classId, "— isPortal:", isPortal);
        const data = isPortal
          ? await getAllSectionsForPortal(form.classId)
          : await getAllSections(form.classId);
        console.log("[StudentForm] Sections received:", data);
        setSections(data || []);
      } catch (err) {
        console.error("[StudentForm] Failed to load sections:", err?.response?.status, err?.response?.data);
      }
    };
    loadSections();
  }, [form.classId, isPortal]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("doc_")) {
      const docKey = name.replace("doc_", "");
      setForm({
        ...form,
        documentsSubmitted: {
          ...form.documentsSubmitted,
          [docKey]: checked,
        },
      });
    } else {
      setForm({ ...form, [name]: type === "checkbox" ? checked : value });
    }
    // Reset section when class changes
    if (name === "classId") {
      setForm((prev) => ({ ...prev, classId: value, sectionId: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("[StudentForm] Submitting form data:", form);
    onSubmit(form);
  };

  const classOptions = [
    { value: "", label: "Select Class" },
    ...classes.map((cls) => ({
      value: cls._id,
      label: `Class ${cls.className}`,
    })),
  ];

  const sectionOptions = [
    { value: "", label: "Select Section" },
    ...sections.map((sec) => ({
      value: sec._id,
      label: `Section ${sec.sectionName}`,
    })),
  ];

  return (
    <Card>
      <CardHeader className="border-b bg-neutral-50/50 flex flex-row items-center justify-between gap-4">
        <CardTitle className="flex items-center gap-2">
          {isEdit ? (
            <UserCheck className="h-5 w-5 text-primary-600 dark:text-primary-400 shrink-0" />
          ) : (
            <UserPlus className="h-5 w-5 text-primary-600 dark:text-primary-400 shrink-0" />
          )}
          <span>{isEdit ? "Update Student Profile" : "New Student Admission"}</span>
        </CardTitle>

        <Button
          type="submit"
          form="student-form"
          variant="primary"
          isLoading={loading}
          className="px-6"
        >
          {loading
            ? isEdit
              ? "Updating..."
              : "Creating..."
            : isEdit
            ? "Update Student"
            : "Create Student"}
        </Button>
      </CardHeader>

      <CardContent className="p-4 sm:p-6">
        <form id="student-form" onSubmit={handleSubmit} className="space-y-6">
          {/* ADMISSION INFO */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 flex items-center gap-1.5">
              <GraduationCap className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              <span>Admission Info</span>
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="Admission No *"
                type="text"
                name="admissionNo"
                value={form.admissionNo}
                onChange={handleChange}
                required
                placeholder="e.g. 2024-001"
              />
              <Input
                label="Roll Number"
                type="text"
                name="rollNumber"
                value={form.rollNumber}
                onChange={handleChange}
                placeholder="e.g. 23"
              />
              <Input
                label="Enrolled On"
                type="date"
                name="enrolledOn"
                value={form.enrolledOn}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* PERSONAL INFO */}
          <div className="border-t border-neutral-200/60 pt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
              Personal Info
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-4">
              <Input
                label="First Name *"
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                required
              />
              <Input
                label="Middle Name"
                type="text"
                name="middleName"
                value={form.middleName}
                onChange={handleChange}
              />
              <Input
                label="Last Name"
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-4">
              <Select
                label="Gender *"
                name="gender"
                value={form.gender}
                onChange={(val) => setForm((prev) => ({ ...prev, gender: val }))}
                options={GENDER_OPTIONS}
              />
              <Input
                label="Date of Birth"
                type="date"
                name="dateOfBirth"
                value={form.dateOfBirth}
                onChange={handleChange}
              />
              <Select
                label="Blood Group"
                name="bloodGroup"
                value={form.bloodGroup}
                onChange={(val) => setForm((prev) => ({ ...prev, bloodGroup: val }))}
                options={BLOOD_GROUP_OPTIONS}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Category"
                type="text"
                name="category"
                value={form.category}
                onChange={handleChange}
                placeholder="e.g. General, OBC, SC, ST"
              />
              <Select
                label="Transport Mode"
                name="transportMode"
                value={form.transportMode}
                onChange={(val) => setForm((prev) => ({ ...prev, transportMode: val }))}
                options={TRANSPORT_OPTIONS}
              />
            </div>
          </div>

          {/* ACADEMIC INFO */}
          <div className="border-t border-neutral-200/60 pt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
              Academic Allocation
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label={`Class * (${classes.length} available)`}
                name="classId"
                value={form.classId}
                onChange={(val) => {
                  setForm((prev) => ({ ...prev, classId: val, sectionId: "" }));
                }}
                options={classOptions}
                required
              />
              <Select
                label={`Section * (${sections.length} available)`}
                name="sectionId"
                value={form.sectionId}
                onChange={(val) => setForm((prev) => ({ ...prev, sectionId: val }))}
                options={sectionOptions}
                disabled={!form.classId}
                required
              />
            </div>
          </div>

          {/* PARENT INFO */}
          <div className="border-t border-neutral-200/60 pt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 flex items-center gap-1.5">
              <Users className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              <span>Parent / Guardian Details</span>
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-4">
              <Input
                label="Father's Name *"
                type="text"
                name="fatherName"
                value={form.fatherName}
                onChange={handleChange}
                required
              />
              <Input
                label="Mother's Name"
                type="text"
                name="motherName"
                value={form.motherName}
                onChange={handleChange}
              />
              <Input
                label="Guardian Name"
                type="text"
                name="guardianName"
                value={form.guardianName}
                onChange={handleChange}
              />
              <Input
                label="Primary Contact *"
                type="text"
                name="primaryContact"
                value={form.primaryContact}
                onChange={handleChange}
                required
              />
              <Input
                label="Secondary Contact"
                type="text"
                name="secondaryContact"
                value={form.secondaryContact}
                onChange={handleChange}
              />
              <Input
                label="Email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-700 dark:text-neutral-200 mb-1.5">
                Address
              </label>
              <textarea
                name="address"
                value={form.address}
                onChange={handleChange}
                rows={2}
                placeholder="Enter residential address..."
                className="w-full rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3 text-xs text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* DOCUMENTS */}
          <div className="border-t border-neutral-200/60 pt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 flex items-center gap-1.5">
              <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              <span>Submitted Verification Documents</span>
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {DOCUMENT_ITEMS.map(({ key, label }) => (
                <label
                  key={key}
                  className="flex items-center gap-2.5 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3 cursor-pointer hover:bg-neutral-50/50 transition-colors"
                >
                  <Checkbox
                    name={`doc_${key}`}
                    checked={!!form.documentsSubmitted[key]}
                    onChange={handleChange}
                  />
                  <span className="text-xs font-medium text-neutral-800 dark:text-neutral-100">
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* REMARKS */}
          <div className="border-t border-neutral-200/60 pt-5">
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1.5 flex items-center gap-1.5">
              <StickyNote className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              <span>Remarks / Additional Notes</span>
            </label>
            <textarea
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              rows={2}
              placeholder="Add any specific observations or comments..."
              className="w-full rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3 text-xs text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default StudentForm;